import React, { useState, useEffect } from 'react';
import axios from '../api/axios';
import { useUser } from '../context/UserContext';

const AdminTemplatesPage = () => {
  const { user } = useUser();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [filters, setFilters] = useState({
    team: '',
    triggerEvent: ''
  });
  const [formData, setFormData] = useState({
    taskName: '',
    team: 'GA',
    triggerEvent: 'SemesterStart',
    deadlineOffset: 0,
    rationale: ''
  });
  const [isMobile, setIsMobile] = useState(false);

  // 권한 체크 헬퍼 함수들
  const canManageTemplate = () => {
    if (!user || !user.role) return false;
    const role = user.role.toUpperCase();
    return ['STAFF', 'LEADER', 'PRESIDENT', 'ADMIN'].includes(role);
  };

  const canDeleteTemplate = () => {
    if (!user || !user.role) return false;
    const role = user.role.toUpperCase();
    return ['PRESIDENT', 'ADMIN'].includes(role);
  };

  // 화면 크기 감지
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      const response = await axios.get('/api/templates');
      setTemplates(response.data);
    } catch (error) {
      alert('Failed to load template list.');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (editingTemplate) {
        // Update
        await axios.put(`/api/templates/${editingTemplate.id}`, formData);
        alert('Template updated successfully.');
      } else {
        // Create
        await axios.post('/api/templates', formData);
        alert('Template created successfully.');
      }
      
      setShowModal(false);
      setEditingTemplate(null);
                  setFormData({
                    taskName: '',
                    team: 'GA',
                    triggerEvent: 'SemesterStart',
                    deadlineOffset: 0,
                    rationale: ''
                  });
      fetchTemplates();
    } catch (error) {
      alert('Failed to save template.');
    }
  };

  const handleEdit = (template) => {
    setEditingTemplate(template);
    setFormData({
      taskName: template.taskName,
      team: template.team,
      triggerEvent: template.triggerEvent,
      deadlineOffset: template.deadlineOffset,
      rationale: template.rationale || ''
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!confirm('Are you sure you want to delete this template task?')) {
      return;
    }
    
    try {
      await axios.delete(`/api/templates/${id}`);
      alert('Template deleted successfully.');
      fetchTemplates();
    } catch (error) {
      alert('Failed to delete template.');
    }
  };

  const openModal = () => {
    setEditingTemplate(null);
                  setFormData({
                    taskName: '',
                    team: 'GA',
                    triggerEvent: 'SemesterStart',
                    deadlineOffset: 0,
                    rationale: ''
                  });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingTemplate(null);
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const getFilteredAndSortedTemplates = () => {
    // First apply filters
    let filteredTemplates = templates.filter(template => {
      const teamMatch = !filters.team || template.team === filters.team;
      const triggerEventMatch = !filters.triggerEvent || template.triggerEvent === filters.triggerEvent;
      return teamMatch && triggerEventMatch;
    });

    // Then apply sorting
    if (!sortConfig.key) return filteredTemplates;

    return filteredTemplates.sort((a, b) => {
      let aValue = a[sortConfig.key];
      let bValue = b[sortConfig.key];

      // Handle different data types
      if (sortConfig.key === 'deadlineOffset') {
        aValue = parseInt(aValue);
        bValue = parseInt(bValue);
      } else if (typeof aValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      if (aValue < bValue) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  };

  const handleFilterChange = (filterType, value) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: value
    }));
  };

  const clearFilters = () => {
    setFilters({
      team: '',
      triggerEvent: ''
    });
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) {
      return (
        <svg className="w-4 h-4 ml-1 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
        </svg>
      );
    }
    
    if (sortConfig.direction === 'asc') {
      return (
        <svg className="w-4 h-4 ml-1 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
        </svg>
      );
    } else {
      return (
        <svg className="w-4 h-4 ml-1 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      );
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-lg">Loading...</div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8 gap-4">
          <div>
            <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-2">Action Plan Template Management</h1>
            <p className="text-gray-600 text-sm sm:text-base">Create and manage action plan templates for events</p>
          </div>
          {canManageTemplate() && (
            <button
              onClick={openModal}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium transition-all duration-200 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 shadow-sm"
            >
              Add New Task
            </button>
          )}
        </div>

        {/* Filters */}
        <div className="bg-white shadow rounded-lg p-4 mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center space-y-4 sm:space-y-0 sm:space-x-4">
            <h3 className="text-sm font-medium text-gray-700">Filters:</h3>
            
            <div className="flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0 sm:space-x-4">
              <div className="flex items-center space-x-2">
                <label className="text-sm text-gray-600">Team:</label>
                <select
                  value={filters.team}
                  onChange={(e) => handleFilterChange('team', e.target.value)}
                  className="px-3 py-1 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">All Teams</option>
                  <option value="GA">GA</option>
                  <option value="PR">PR</option>
                  <option value="EP">EP</option>
                  <option value="Collective">Collective</option>
                </select>
              </div>

              <div className="flex items-center space-x-2">
                <label className="text-sm text-gray-600">Trigger Event:</label>
                <select
                  value={filters.triggerEvent}
                  onChange={(e) => handleFilterChange('triggerEvent', e.target.value)}
                  className="px-3 py-1 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">All Events</option>
                  <option value="SemesterStart">Semester Start</option>
                  <option value="ExpoDay">Expo Day</option>
                  <option value="MeetDay">Meet Day</option>
                  <option value="Outing">Outing</option>
                </select>
              </div>

              <button
                onClick={clearFilters}
                className="px-3 py-1 text-sm text-gray-600 hover:text-gray-800 border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Clear Filters
              </button>
            </div>

            <div className="text-sm text-gray-500">
              Showing {getFilteredAndSortedTemplates().length} of {templates.length} templates
            </div>
          </div>
        </div>

        {isMobile ? (
          // Mobile Card View
          <div className="space-y-4">
            {getFilteredAndSortedTemplates().map((template) => (
              <div key={template.id} className="bg-white p-4 rounded-lg shadow-md border">
                <div className="space-y-3">
                  <div>
                    <h3 className="font-semibold text-gray-900">{template.taskName}</h3>
                    <div className="flex flex-wrap gap-2 mt-2">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        template.team === 'GA' ? 'bg-green-100 text-green-800' :
                        template.team === 'PR' ? 'bg-blue-100 text-blue-800' :
                        template.team === 'EP' ? 'bg-purple-100 text-purple-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {template.team}
                      </span>
                      <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-gray-100 text-gray-800">
                        {template.triggerEvent === 'SemesterStart' ? 'Semester Start' :
                         template.triggerEvent === 'ExpoDay' ? 'Expo Day' :
                         template.triggerEvent === 'MeetDay' ? 'Meet Day' :
                         template.triggerEvent === 'Outing' ? 'Outing' : template.triggerEvent}
                      </span>
                    </div>
                  </div>
                  <div className="text-sm text-gray-600">
                    <span className="font-medium">Deadline:</span> {template.deadlineOffset > 0 ? `+${template.deadlineOffset} days` : `${template.deadlineOffset} days`}
                  </div>
                  <div className="text-sm text-gray-600">
                    <span className="font-medium">Rationale:</span> {template.rationale || 'No rationale provided'}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {canManageTemplate() && (
                      <button
                        onClick={() => handleEdit(template)}
                        className="text-indigo-600 hover:text-indigo-900 text-sm font-medium"
                      >
                        Edit
                      </button>
                    )}
                    {canDeleteTemplate() && (
                      <button
                        onClick={() => handleDelete(template.id)}
                        className="text-red-600 hover:text-red-900 text-sm font-medium"
                      >
                        Delete
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
            {getFilteredAndSortedTemplates().length === 0 && (
              <div className="text-center py-12">
                <p className="text-gray-500">No templates found</p>
              </div>
            )}
          </div>
        ) : (
          // Desktop Table View
          <div className="bg-white shadow rounded-lg overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleSort('taskName')}
                  >
                    <div className="flex items-center">
                      Task Name
                      {getSortIcon('taskName')}
                    </div>
                  </th>
                  <th 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleSort('team')}
                  >
                    <div className="flex items-center">
                      Team
                      {getSortIcon('team')}
                    </div>
                  </th>
                  <th 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleSort('triggerEvent')}
                  >
                    <div className="flex items-center">
                      Trigger Event
                      {getSortIcon('triggerEvent')}
                    </div>
                  </th>
                  <th 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleSort('deadlineOffset')}
                  >
                    <div className="flex items-center">
                      Deadline Offset
                      {getSortIcon('deadlineOffset')}
                    </div>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-96">
                    Rationale
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {getFilteredAndSortedTemplates().map((template) => (
                  <tr key={template.id}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {template.taskName}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        template.team === 'GA' ? 'bg-green-100 text-green-800' :
                        template.team === 'PR' ? 'bg-blue-100 text-blue-800' :
                        template.team === 'EP' ? 'bg-purple-100 text-purple-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {template.team}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {template.triggerEvent === 'SemesterStart' ? 'Semester Start' :
                       template.triggerEvent === 'ExpoDay' ? 'Expo Day' :
                       template.triggerEvent === 'MeetDay' ? 'Meet Day' :
                       template.triggerEvent === 'Outing' ? 'Outing' : template.triggerEvent}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {template.deadlineOffset > 0 ? `+${template.deadlineOffset} days` : `${template.deadlineOffset} days`}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500 w-96">
                      <div className="break-words" title={template.rationale || 'No rationale provided'}>
                        {template.rationale || 'No rationale provided'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      {canManageTemplate() && (
                        <button
                          onClick={() => handleEdit(template)}
                          className="text-indigo-600 hover:text-indigo-900 mr-3"
                        >
                          Edit
                        </button>
                      )}
                      {canDeleteTemplate() && (
                        <button
                          onClick={() => handleDelete(template.id)}
                          className="text-red-600 hover:text-red-900"
                        >
                          Delete
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-5 border w-full max-w-md shadow-lg rounded-md bg-white">
              <div className="mt-3">
                <h3 className="text-lg font-medium text-gray-900 mb-4">
                  {editingTemplate ? 'Edit Template' : 'Add New Template'}
                </h3>
                
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Task Name
                    </label>
                    <input
                      type="text"
                      value={formData.taskName}
                      onChange={(e) => setFormData({...formData, taskName: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Team
                    </label>
                    <select
                      value={formData.team}
                      onChange={(e) => setFormData({...formData, team: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="GA">GA</option>
                      <option value="PR">PR</option>
                      <option value="EP">EP</option>
                      <option value="Collective">Collective</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Trigger Event
                    </label>
                    <select
                      value={formData.triggerEvent}
                      onChange={(e) => setFormData({...formData, triggerEvent: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                                  <option value="SemesterStart">Semester Start</option>
                                  <option value="ExpoDay">Expo Day</option>
                                  <option value="MeetDay">Meet Day</option>
                                  <option value="Outing">Outing</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Deadline Offset (days)
                    </label>
                    <input
                      type="number"
                      value={formData.deadlineOffset}
                      onChange={(e) => setFormData({...formData, deadlineOffset: parseInt(e.target.value)})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Negative values mean before the trigger event, positive values mean after.
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Rationale
                    </label>
                    <textarea
                      value={formData.rationale}
                      onChange={(e) => setFormData({...formData, rationale: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      rows={3}
                      placeholder="Explain why this task is necessary..."
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Explain the purpose and importance of this task.
                    </p>
                  </div>
                  
                  <div className="flex justify-end space-x-3 pt-4">
                    <button
                      type="button"
                      onClick={closeModal}
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md"
                    >
                      {editingTemplate ? 'Update' : 'Add'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}
      </div>
  );
};

export default AdminTemplatesPage;
